import { Countries, Currencies } from '@constants/enums';
import { Button } from '@efexteam/components';
import { Card, CardContent, Skeleton } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Input from '../../components/common/Form/Input';
import {
  COUNTRY_CURRENCY,
  CURRENCY_OPTIONS,
} from '../../components/common/Form/config';
import { CountryBankAccount } from '../../components/types';
import useBoolean from '../../hooks/useBoolean';
import {
  IBankAccountDetails,
  ICompanyDetails,
} from '../../services/auth/types';
import { displayValue } from '../../utils/formater';
import { selectProfileData } from './store/selectors';

interface BankAccountDetailsProps {
  bankAccount?: IBankAccountDetails;
  company?: ICompanyDetails;
  loading: boolean;
  onSubmit: (data: IBankAccountDetails, blockEdited: 'bank') => void;
}

const BankAccountDetails = ({
  bankAccount,
  company,
  loading,
  onSubmit,
}: BankAccountDetailsProps) => {
  const { t } = useTranslation();
  const userProfile = useSelector(selectProfileData);
  const [isEditActive, { toggle: toggleIsEditState }] = useBoolean(false);
  const [form] = useState<IBankAccountDetails>();

  const fixedCountryIncludingAccountCurrency = useMemo(
    () =>
      company?.accountCurrency === Currencies.USD
        ? Countries.USA
        : company?.country || 'USA',
    [company?.accountCurrency, company?.country]
  );

  const typeAccount:
    | CountryBankAccount.MX
    | CountryBankAccount.COL
    | CountryBankAccount.USA =
    CountryBankAccount[fixedCountryIncludingAccountCurrency];

  const countryCurrency =
    company?.accountCurrency ??
    COUNTRY_CURRENCY[fixedCountryIncludingAccountCurrency || Countries.USA];

  const country = CURRENCY_OPTIONS.find(
    (option) => option.id === countryCurrency
  );

  const noEditable = true;

  const handleEdit = () => {
    if (isEditActive && form) {
      onSubmit(form, 'bank');
    }
    toggleIsEditState();
  };

  if (loading) {
    return <Skeleton variant="rectangular" height={100} />;
  }

  return (
    <Card className="my-4" title={t('bankAccountLinked') ?? ''}>
      <CardContent>
        <div className="flex flex-wrap justify-between">
          <div className="w-1/2 md:w-1/4 px-1 mb-2">
            <p className="text-xs text-label-light mb-2">{t('bankName')}</p>
            <p className="text-sm">{displayValue(bankAccount?.bankName)}</p>
          </div>
          {fixedCountryIncludingAccountCurrency === Countries.USA && (
            <div className="w-1/2 md:w-1/4 px-1 mb-2">
              <p className="text-xs text-label-light mb-2">
                {t('bankAccount')}
              </p>

              {!isEditActive ? (
                <p className="text-sm">
                  {displayValue(bankAccount?.accountNumber)}
                </p>
              ) : (
                <Input
                  autoFocus
                  name="accountNumber"
                  defaultValue={bankAccount?.accountNumber}
                  className="text-sm mb-2"
                  readOnly
                />
              )}
            </div>
          )}
          <div className="w-1/2 md:w-1/4 px-1 mb-2">
            <p className="text-xs text-label-light mb-2">
              {t(
                `profileContactAccount${
                  userProfile?.company.country ?? Countries.USA
                }`
              )}
            </p>
            {!isEditActive ? (
              <p className="text-sm">
                {bankAccount ? displayValue(bankAccount[typeAccount]) : '-'}
              </p>
            ) : (
              <Input
                autoFocus
                name="routingNumber"
                defaultValue={bankAccount ? bankAccount[typeAccount] : ''}
                className="text-sm mb-2"
                readOnly
              />
            )}
          </div>
          <div className="w-1/2 md:w-1/4">
            <p className="text-xs text-label-light mb-2">
              {t('defaultCurrency')}
            </p>
            <div className="p-1.5 rounded-none bg-primary-fill flex items-center">
              {country?.icon || CURRENCY_OPTIONS[0].icon}
              <p className="m-0 text-sm">
                {country?.value || CURRENCY_OPTIONS[0].value}
              </p>
            </div>
          </div>
          <div className="w-1/2 md:w-1/4 text-right flex flex-col items-end  !invisible">
            {!noEditable && (
              <Button
                onClick={handleEdit}
                className="w-full md:max-w-[120px] text-sm mt-2"
                appearance="secondary"
              >
                {isEditActive ? t('update') : t('edit')}
              </Button>
            )}
            {isEditActive && (
              <Button
                onClick={toggleIsEditState}
                className="w-full md:max-w-[120px] text-sm mt-2"
                appearance="primary"
              >
                {t('close')}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
export default BankAccountDetails;
